/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { useTransactionSummary } from '../hooks/use-transaction-summary';
import { Activity, ArrowUpRight, ArrowDownRight, Layers, Box, CheckCircle, Flame, Lock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const getIcon = (type: 'MINT' | 'SWAP' | 'LIQUIDITY' | 'BURN') => {
    switch(type) {
        case 'MINT': return <Box size={14} />;
        case 'SWAP': return <ArrowUpRight size={14} />;
        case 'LIQUIDITY': return <Layers size={14} />;
        case 'BURN': return <Flame size={14} />;
        default: return <Activity size={14} />;
    }
};

export const TransactionSummary: React.FC = () => {
    const { transactions, loading } = useTransactionSummary();

    if (loading && transactions.length === 0) return (
        <div className="bg-white/50 backdrop-blur-md rounded-2xl border border-stone-200 shadow-sm h-full flex flex-col p-6 animate-pulse">
            <div className="h-4 bg-stone-200 rounded w-1/2 mb-6"></div>
            {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-3 py-3 border-b border-stone-100 last:border-0">
                    <div className="w-8 h-8 rounded-full bg-stone-200"></div>
                    <div className="flex-1 space-y-2">
                        <div className="h-3 bg-stone-200 rounded w-3/4"></div>
                        <div className="h-2 bg-stone-100 rounded w-1/2"></div>
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <div className="bg-white/60 backdrop-blur-md rounded-2xl border border-stone-200 shadow-soft-lg overflow-hidden h-full flex flex-col">
            <div className="p-4 border-b border-stone-200 flex justify-between items-center bg-white/70">
                <h3 className="font-serif font-bold text-stone-900 flex items-center gap-2">
                    <Activity size={18} className="text-nobel-gold" />
                    Live Transaction Feed
                </h3>
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
            </div>
            
            <div className="flex-1 overflow-y-auto custom-scrollbar p-2">
                <AnimatePresence initial={false}>
                    {transactions.map((tx) => (
                        <motion.div 
                            key={tx.id} 
                            layout
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.3 }}
                            className="p-2 border-b border-stone-100 last:border-0 hover:bg-stone-100/50 rounded-md flex items-center gap-3"
                        >
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                                tx.type === 'MINT' ? 'bg-purple-100 text-purple-600' :
                                tx.type === 'SWAP' ? 'bg-blue-100 text-blue-600' :
                                tx.type === 'LIQUIDITY' ? 'bg-amber-100 text-amber-600' :
                                'bg-stone-100 text-stone-600'
                            }`}>
                                {getIcon(tx.type)}
                            </div>
                            
                            <div className="flex-1 min-w-0">
                                <div className="flex justify-between items-center mb-0.5">
                                    <span className="font-bold text-xs text-stone-900 truncate">{tx.description}</span>
                                    <div className="flex items-center gap-1.5 shrink-0 ml-2">
                                        <span className="text-[10px] text-stone-400 font-mono">
                                            {Math.floor((Date.now() - tx.timestamp) / 1000)}s ago
                                        </span>
                                        <img src={tx.chain === 'SUI' ? 'https://cryptologos.cc/logos/sui-sui-logo.png' : 'https://cryptologos.cc/logos/solana-sol-logo.png'} className="w-3 h-3" alt={tx.chain} />
                                    </div>
                                </div>
                                <div className="flex justify-between items-center">
                                    <div className="text-[10px] text-stone-500 font-mono flex items-center gap-1">
                                        By: {tx.user}
                                    </div>
                                    {tx.amount && (
                                        <span className="text-[10px] font-bold text-stone-700 bg-stone-100 px-1 rounded-sm border border-stone-200">
                                            {tx.amount} {tx.tokenSymbol}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>
        </div>
    );
};
